import React, { useEffect, useMemo, useState } from "react";
import { Card, PageHeader, StatusPill, Empty, inputCls } from "../../components/dash/Kit";
import { api } from "../../lib/api";

export default function AdminCalendar() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userId, setUserId] = useState("");
  const [cursor, setCursor] = useState(() => new Date());

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/admin/content", { params: userId ? { user_id: userId } : {} })
      .then((r) => setItems(r.data))
      .catch(() => {});
  }, [userId]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthKey = `${year}-${String(month + 1).padStart(2, "0")}`;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const offset = (new Date(year, month, 1).getDay() + 6) % 7;

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((i) => {
      if (!i.post_date?.startsWith(monthKey)) return;
      const d = Number(i.post_date.slice(8, 10));
      map[d] = map[d] || [];
      map[d].push(i);
    });
    return map;
  }, [items, monthKey]);

  return (
    <div className="space-y-6" data-testid="admin-calendar-page">
      <PageHeader
        title="Content Calendar"
        body="All scheduled content across customers."
        action={
          <select data-testid="admin-calendar-customer" className={`${inputCls} sm:w-64`} value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">All customers</option>
            {customers.map((c) => (<option key={c.id} value={c.id}>{c.business_name}</option>))}
          </select>
        }
      />

      <Card testId="admin-calendar-grid">
        <div className="flex items-center justify-between">
          <button type="button" data-testid="admin-calendar-prev" onClick={() => setCursor(new Date(year, month - 1, 1))} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold">
            Previous
          </button>
          <p className="font-display text-lg font-semibold text-slate-900">
            {new Date(year, month, 1).toLocaleString("en-GB", { month: "long", year: "numeric" })}
          </p>
          <button type="button" data-testid="admin-calendar-next" onClick={() => setCursor(new Date(year, month + 1, 1))} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold">
            Next
          </button>
        </div>

        <div className="mt-5 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-slate-400">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (<span key={d}>{d}</span>))}
        </div>
        <div className="mt-1 grid grid-cols-7 gap-1">
          {Array.from({ length: offset }).map((_, i) => (
            <div key={`pad-${i}`} className="min-h-[80px] rounded-lg bg-slate-50/60" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const entries = byDay[day] || [];
            return (
              <div key={day} data-testid={`admin-calendar-day-${day}`} className="min-h-[80px] rounded-lg border border-slate-200 p-1.5">
                <span className="text-[11px] font-semibold text-slate-400">{day}</span>
                <div className="mt-1 space-y-1">
                  {entries.slice(0, 3).map((e) => (
                    <span key={e.id} className="block truncate rounded bg-[#FFF3EF] px-1 py-0.5 text-[10px] font-semibold text-[#B93615]">
                      {e.content_type}
                    </span>
                  ))}
                  {entries.length > 3 && <span className="text-[10px] text-slate-400">+{entries.length - 3}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {items.length === 0 ? (
        <Empty testId="admin-calendar-empty" title="No scheduled content" />
      ) : (
        <div className="space-y-2" data-testid="admin-calendar-list">
          {items.slice(0, 40).map((i) => (
            <Card key={i.id} testId={`admin-calendar-item-${i.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm text-slate-800">{i.post_date} · {i.platform} · {i.content_type}</span>
                <StatusPill status={i.status} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
